import {Box} from '@mui/material'
import {isString} from 'lodash'
import moment, {Moment} from 'moment-timezone'
import React, {useMemo} from 'react'

import {filterSxStyles} from '../../styles'
import {
  DateRange,
  Shift,
  ShiftQuickSelectSlots,
  SHIFT_QUICK_SELECT_SLOTS,
  SHIFT_QUICK_SELECT_SLOT_LIST
} from '../../types'
import {getAllPossibleShiftsOfDay, getQuickSelectFilter} from '../../utils'

import {ShiftDatePicker} from './ShiftDatePicker'
type DatePickerProps = {
  onChange: (value: ShiftQuickSelectSlots | DateRange) => void
  value: ShiftQuickSelectSlots | DateRange | undefined
  shifts: Shift[]
  timezone: string
  plantNow: Moment
  defaultTimeRange: ShiftQuickSelectSlots | undefined
  translate: (key: string) => string
  language: string
  quickSelectSlots?: ShiftQuickSelectSlots[]
  disableDateRangeSelect?: boolean
  disableTimeOnMultiDay?: boolean
}

type DateRangeArray = [Moment, Moment]

export const FilterDatePicker: React.FC<DatePickerProps> = (props) => {
  const {shifts, plantNow, defaultTimeRange, timezone, translate, language, quickSelectSlots} =
    props

  // Default quick select option is currentShift
  const {onChange, value: selectedOption = defaultTimeRange} = props

  const state = useMemo(() => {
    let date: Moment | undefined
    let quickSelectId: ShiftQuickSelectSlots | undefined
    let dateRange: DateRangeArray | undefined
    if (selectedOption && !isString(selectedOption)) {
      // Date or a date range is selected
      const {startDate, endDate}: DateRange = selectedOption
      if (startDate && endDate) {
        // In case of whole day selection, starting time is picked to calculate date
        const diff = moment.duration(endDate.diff(startDate)).asHours()
        if (diff >= 24) {
          dateRange = [startDate, endDate]
        } else {
          date = startDate
        }
      }
    } else {
      // Quick select option is selected
      quickSelectId = selectedOption

      // If applied quick select filter key does not exist, fall back to last 30 days
      if (
        !quickSelectId ||
        !SHIFT_QUICK_SELECT_SLOT_LIST.includes(quickSelectId as SHIFT_QUICK_SELECT_SLOTS)
      ) {
        quickSelectId = SHIFT_QUICK_SELECT_SLOTS.Last30days
      }

      // calculate quick select corresponding date for calender
      const [, end] = getQuickSelectFilter(quickSelectId, shifts, plantNow)
      // subtracted 1 min to always fall in the shift duration
      if (end) date = moment(end).clone().subtract(1, 'minute')
    }

    return {
      quickSelectId,
      date,
      dateRange
    }
  }, [selectedOption, shifts, plantNow])

  const {quickSelectId, date, dateRange} = state

  return (
    <Box sx={filterSxStyles.dateFilter} data-test-id="simple-shift-picker">
      <ShiftDatePicker
        translate={translate}
        language={language}
        quickSelectId={quickSelectId}
        date={date}
        dateRange={dateRange}
        onChange={onChange}
        timezone={timezone}
        shifts={shifts}
        plantNow={plantNow}
        quickSelectSlots={quickSelectSlots}
        isOutsideRange={(date) => !getAllPossibleShiftsOfDay(date, shifts, true).list.length}
        disableDateRangeSelect={props.disableDateRangeSelect}
        disableTimeOnMultiDay={props.disableTimeOnMultiDay}
      />
    </Box>
  )
}
